import Service, { type Registry as Services, service } from '@ember/service';
import {
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signInWithRedirect,
} from 'firebase/auth';
import ENV from 'frontend-login/config/environment';
import type { GIPName } from 'frontend-login/utils/constant-values';
import { RESPONSE_CODE } from 'frontend-login/utils/constant-values';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import Cookies from 'js-cookie';
import type {
  LoginResponse,
  MfaInfo,
} from 'password-form/services/mfa-service';
import nextWithDefault from 'password-form/utils/auth-utils';

type ParamsType = {
  next?: string;
};

export default class AuthenticationService extends Service {
  @service declare customFetch: Services['custom-fetch'];
  @service declare firebaseService: Services['firebase-service'];
  @service declare router: Services['router'];

  /**
   * Flag to prevent multiple redirects after the user has been logged in with their idp.
   */
  isRedirectProcessed = false;

  /**
   * This function sends a request to BE to accept the selected SSO method as new login method.
   * The method can be changed either by themselves or set by their manager.
   *
   * @context If user is changed by their admin to be an enforced SSO user, they still need to accept or deny the change.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-redundant-type-constituents
  async acceptSso(token: string, gipName: GIPName | string) {
    return fetch(
      `${getHostDomain({ frontendGateway: ENV.frontendGateway })}/sso/accept-sso/`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': Cookies.get('csrftoken')!,
        },
        body: JSON.stringify({
          token,
          gip_name: gipName,
        }),
      },
    );
  }

  /**
   * Gets the redirect result from the authentication provider.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleRedirect() {
    if (this.isRedirectProcessed) {
      return null;
    }

    this.isRedirectProcessed = true;
    return getRedirectResult(this.firebaseService.auth);
  }

  /**
   * Type guard to check if the response is an error object
   */
  isErrorResponse(
    response: unknown,
  ): response is { code: string; detail?: string; title?: string } {
    return Boolean(
      response && typeof response === 'object' && 'code' in response,
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async redirectToMfa(next: string): Promise<void> {
    const queryParams: ParamsType = {};
    if (next) {
      queryParams.next = nextWithDefault(next);
    }

    this.router.transitionTo('mfa', {
      queryParams,
    });
  }

  /**
   * Checks if the user should be redirected to the MFA flow.
   *
   * This is based on if the user has MFA enforced or if they have seen the MFA screen before.
   * If the user has a GIP display name (i.e. a method already enable), they should not see the MFA selection screen.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async shouldShowMfa(email: string, gipName: string | null) {
    let mfaInfoParams: { email: string; gip_name?: string | null } = { email };

    if (gipName) {
      mfaInfoParams = {
        ...mfaInfoParams,
        gip_name: gipName,
      };
    }

    const mfaInfo: MfaInfo = await this.customFetch.fetch(
      '/sso/mfa-info/',
      mfaInfoParams,
    );

    if (this.isErrorResponse(mfaInfo)) {
      if (mfaInfo.code === RESPONSE_CODE.IDENTITY_PROVIDER_NOT_FOUND) {
        throw new Error('IDENTITY_PROVIDER_NOT_FOUND');
      }
      // Handle other API errors
      throw new Error(`MFA info request failed: ${mfaInfo.code}`);
    }

    // Return early if GIP is configured
    if (mfaInfo.gip_display_name) {
      return false;
    }

    // Show MFA if enforced or never shown before
    return mfaInfo.enforce_mfa || !mfaInfo.mfa_was_shown;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    return signInWithRedirect(this.firebaseService.auth, provider);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async signInWithMicrosoft() {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      prompt: 'select_account',
    });
    return signInWithRedirect(this.firebaseService.auth, provider);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async signInWithPassword(email: string, password: string) {
    return signInWithEmailAndPassword(
      this.firebaseService.auth,
      email,
      password,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async signInWithSaml(gipName: string) {
    const provider = new SAMLAuthProvider(gipName);
    return signInWithRedirect(this.firebaseService.auth, provider);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async verifyTokenOnUM(token: string, params: Record<string, unknown> = {}) {
    return this.customFetch.post<LoginResponse>('/sso/login/', params, {
      token,
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    authentication: AuthenticationService;
  }
}
